import { type I18n } from "@common/lib/i18n";
import { Common } from "@common/types";
import { isFunction } from "lodash";
import React from "react";
import { type SelectContentProps, type SelectProps, type ValueType } from "./component";

export const EMPTY_OPTION = {
  label: "",
  value: undefined
};

export const getAvailableOptions = <T, TOption extends Common.OptionItem<T>>(
  props: SelectContentProps<T, TOption>
): Common.OptionItem<any>[] => {
  if (!props.options.length) return [];

  let options = props.options.slice();
  if (props.excludeOptionValues) options = options.filter((x) => !props.excludeOptionValues!.includes(x.value));
  if (props.includeOptionValues) options = options.filter((x) => props.includeOptionValues!.includes(x.value));
  return options;
};

export const getSelectedLabel = <T, TOption extends Common.OptionItem<T>>(
  i18n: I18n,
  options: Common.OptionItem<any>[],
  props: SelectProps<T, TOption>
): any => {
  const option = getSelectedOption(props);
  if (!options.length) return props.blankOptionLabel ?? option?.label ?? i18n.noOptionsAvailable;

  if (option && isFunction(props.selectedLabel)) return props.selectedLabel(option);
  if (props.selectedLabel && !isFunction(props.selectedLabel)) return props.selectedLabel;

  return option
    ? option.label
    : props.isRequired && !props.placeholder
    ? i18n.pleaseSelect
    : props.placeholder ?? props.emptyLabel ?? "";
};

export const getSelectedOption = <T, TOption extends Common.OptionItem<T>>(
  props: SelectProps<T, TOption>
): TOption | undefined =>
  props.allOptions.find((opt) =>
    props.outputType === "option"
      ? isSameValue((props.value as Common.OptionItem<ValueType>)?.value, opt.value)
      : isSameValue(props.value, opt.value)
  );

export const isSameValue = (value: any, optionValue: any): boolean => {
  if (value instanceof Date) return value.valueOf() === new Date(optionValue).valueOf();
  return value == optionValue; // IMPORTANT! use == to avoid type casting
};

export const optionItemComparer = (a: Common.OptionItem<ValueType>, b: Common.OptionItem<ValueType>): boolean =>
  a.value == b.value;

export const LINK_STYLE: React.CSSProperties = {
  border: "none",
  color: "var(--blue)",
  padding: 0
};

export const calculateMaxHeight = (maxHeight, heading) => maxHeight ?? 195 + (heading ?? []).length * 36;
